import { createSlice } from '@reduxjs/toolkit';
import { agentState } from '../../../constants/agents/initialStates/agentState';
import totalAgentTypes from '../../../constants/agents/totalAgentTypes';
import {
  KA_SMART_CHAT_AURORA_TEMPLATE,
  KA_SMART_CHAT_VOICE_TEMPLATE,
} from '../../../constants/agents/templateTypes';
import {
  VOICE_CONFIG_OFF,
  VOICE_CONFIG_ON,
  voiceForCampaignLanguage,
} from '../../../constants/agents/voiceConfig';
import { agentFormatOf } from '../../../constants/agents/connectionAgent';

/** Option ids used by poll CTA mapping UI: `opt._id ?? \`${eng._id}-${index}\`` */
export function collectPollCtaOptionIdsFromEngagements(engagements) {
  const ids = new Set();
  if (!Array.isArray(engagements)) return ids;
  for (const eng of engagements) {
    const options = eng?.engagementData?.options ?? [];
    options.forEach((opt, index) => {
      ids.add(opt._id ?? `${eng._id}-${index}`);
    });
  }
  return ids;
}

function prunePollCtaMappedOptions(state) {
  const engagements = state.data.campaignData?.engagements;
  if (!Array.isArray(engagements)) return;
  const validIds = collectPollCtaOptionIdsFromEngagements(engagements);
  const mapped = state.etc.pollCtaMapping.mappedOptions;
  if (!Array.isArray(mapped) || mapped.length === 0) return;
  state.etc.pollCtaMapping.mappedOptions = mapped.filter(m => validIds.has(m.optionId));
}

// New website Knowledge agents start with voice on, in the campaign language's voice. Agents
// being edited keep what they were saved with (off when the record has no voiceConfig).
function seedNewKnowledgeAgentVoice(state) {
  const campaignData = state.data?.campaignData;
  if (!campaignData || campaignData.campaignType !== 'rag' || campaignData.voiceConfig) return;
  const isEditing = state.etc.creationType === 'edit' || state.etc.creationType === 'agentEdit';
  campaignData.voiceConfig = isEditing
    ? { ...VOICE_CONFIG_OFF }
    : { ...VOICE_CONFIG_ON, lang: voiceForCampaignLanguage(state.data.visualization?.language) };
}

// A Connection format (or one to one pairing) turned off keeps its engine here, so turning it
// back on in the same setup picks the same engine again.
const keepRemovedSlot = (state, key, slot) => {
  if (!slot?.engineId) return;
  state.connectionAgent.removedSlots = { ...state.connectionAgent.removedSlots, [key]: slot };
};
const takeRemovedSlot = (state, key) => {
  const slot = state.connectionAgent.removedSlots?.[key];
  if (slot) {
    const { [key]: _taken, ...rest } = state.connectionAgent.removedSlots;
    state.connectionAgent.removedSlots = rest;
  }
  return slot ? { ...slot } : null;
};

export const agentSlice = createSlice({
  name: 'agent',
  initialState: agentState,
  reducers: {
    setAgentData: (state, action) => {
      state.data = action.payload;
      const object = totalAgentTypes?.find(
        item => item.value === action?.payload?.campaignData?.campaignType
      );
      if (object) {
        state.etc.agentType = object?.agentType;
      }
      if (state.data.campaignData?.campaignType === 'rag') {
        state.data.campaignData.engagementGenerationByAI = true;
      }
      seedNewKnowledgeAgentVoice(state);
      prunePollCtaMappedOptions(state);
    },
    setAgentEtc: (state, action) => {
      state.etc = action.payload;
    },
    setAgentStep: (state, action) => {
      state.etc.agentStep = action.payload;
    },
    setNextAgentStep: state => {
      state.etc.agentStep += 1;
    },
    setPrevAgentStep: state => {
      state.etc.agentStep -= 1;
    },

    setGoalSubStep: (state, action) => {
      state.etc.goalSubStep = action.payload;
    },

    setSelectedPlaybook: (state, action) => {
      state.etc.selectedPlaybook = action.payload;
    },
    setPlaybookId: (state, action) => {
      state.data.playbookId = action.payload;
    },

    //goal related
    setAgentGoal: (state, action) => {
      state.data.campaignData.campaignType = action.payload;
      state.aiAgent.aiAgentData.aiAgentType = action.payload;
      const object = totalAgentTypes?.find(item => item.value === action?.payload);
      if (object) {
        state.etc.agentType = object?.agentType;
      }
      if (action.payload === 'rag') {
        state.data.campaignData.engagementGenerationByAI = true;
      }
      seedNewKnowledgeAgentVoice(state);
    },

    //define related

    setAgentDomainConfig: (state, action) => {
      state.data.domainConfig = action.payload;
    },

    setAgentDomainType: (state, action) => {
      state.data.domainConfig.type = action.payload;
    },

    setAgentName: (state, action) => {
      state.data.title = action.payload;
    },

    setAgentDomain: (state, action) => {
      state.data.domainConfig.domainId = action.payload;
    },

    setAgentDomainPaths: (state, action) => {
      state.data.domainConfig.paths = action.payload;
    },

    setAgentDomainExceptions: (state, action) => {
      state.data.domainConfig.exceptions = action.payload;
    },

    setPageMetaDataDomainConfigFromPublishedDate: (state, action) => {
      state.data.domainConfig.pageMetaDataDomainConfig.fromPublishedDate = action.payload;
    },

    setPageMetaDataDomainConfigCategories: (state, action) => {
      state.data.domainConfig.pageMetaDataDomainConfig.categories = action.payload;
    },

    setPageMetaDataDomainConfigTags: (state, action) => {
      state.data.domainConfig.pageMetaDataDomainConfig.tags = action.payload;
    },

    setAgentLanguage: (state, action) => {
      // The voice follows the campaign language, unless the organiser picked another one.
      const voiceConfig = state.data.campaignData?.voiceConfig;
      if (
        voiceConfig &&
        voiceConfig.lang === voiceForCampaignLanguage(state.data.visualization.language)
      ) {
        voiceConfig.lang = voiceForCampaignLanguage(action.payload);
      }
      state.data.visualization.language = action.payload;
      state.data.aiEngagementsNeedUpdate = true;
    },

    //--------------------------------design related--------------------------------

    // * placement related

    setCurrentSelectedPlacement: (state, action) => {
      state.etc.currentSelectedPlacement = action.payload;
    },

    setCampaignPlacements: (state, action) => {
      state.data.placements = action.payload;
    },

    setSidePanelButtonStyleDesktop: (state, action) => {
      const index = (state.data.placements || []).findIndex(
        p => (p.deviceType || 'desktop') === 'desktop'
      );
      if (index < 0 || !state.data.placements[index]?.placementData) return;
      state.data.placements[index].placementData.buttonStyle = action.payload;
    },

    setSidePanelButtonStyleMobile: (state, action) => {
      const index = (state.data.placements || []).findIndex(p => p.deviceType === 'mobile');
      if (index < 0 || !state.data.placements[index]?.placementData) return;
      state.data.placements[index].placementData.buttonStyle = action.payload;
    },

    setSidePanelButtonPositionDesktop: (state, action) => {
      const index = (state.data.placements || []).findIndex(
        p => (p.deviceType || 'desktop') === 'desktop'
      );
      if (index < 0 || !state.data.placements[index]?.placementData) return;
      state.data.placements[index].placementData.position = action.payload;
    },

    setSidePanelButtonPositionMobile: (state, action) => {
      const index = (state.data.placements || []).findIndex(p => p.deviceType === 'mobile');
      if (index < 0 || !state.data.placements[index]?.placementData) return;
      state.data.placements[index].placementData.position = action.payload;
    },

    setAgentSpotlightAnimation: (state, action) => {
      const index = (state.data.placements || []).findIndex(
        p => (p.deviceType || 'desktop') === 'desktop'
      );
      if (index < 0) return;
      state.data.placements[index].spotLightAnimation = action.payload;
    },
    setAgentSpotlightAnimationMobile: (state, action) => {
      const index = (state.data.placements || []).findIndex(p => p.deviceType === 'mobile');
      if (index < 0) return;
      state.data.placements[index].spotLightAnimation = action.payload;
    },

    setAgentIgnoreCLS: (state, action) => {
      const index = (state.data.placements || []).findIndex(
        p => (p.deviceType || 'desktop') === 'desktop'
      );
      if (index < 0) return;
      state.data.placements[index].ignoreCLS = action.payload;
    },
    setAgentIgnoreCLSMobile: (state, action) => {
      const index = (state.data.placements || []).findIndex(p => p.deviceType === 'mobile');
      if (index < 0) return;
      state.data.placements[index].ignoreCLS = action.payload;
    },

    setAgentEngagementViewLimit: (state, action) => {
      state.data.campaignData.engagementViewLimit = action.payload;
    },

    // * visualization related

    setAgentVisualization: (state, action) => {
      state.data.visualization = action.payload;
    },
    // Theme related
    setAgentPrimaryColor: (state, action) => {
      state.data.visualization.primaryColor = action.payload;
    },
    setAgentDarkTheme: (state, action) => {
      state.data.visualization.darkTheme = action.payload;
    },

    setAgentAnimatedBorder: (state, action) => {
      state.data.visualization.animatedBorder = action.payload;
    },
    setAgentCardSizeDesktop: (state, action) => {
      state.data.visualization.cardSize = action.payload;
    },
    setAgentCardSizeMobile: (state, action) => {
      state.data.visualization.cardSizeMobile = action.payload;
    },
    setIsDefaultMargin: (state, action) => {
      state.etc.isDefaultMargin = action.payload;
    },
    setIsDefaultCornerRadius: (state, action) => {
      state.etc.isDefaultCornerRadius = action.payload;
    },

    // Font related
    setAgentFontScaleDesktop: (state, action) => {
      state.data.visualization.fontScale = action.payload;
    },
    setAgentFontScaleMobile: (state, action) => {
      state.data.visualization.fontScaleMobile = action.payload;
    },
    setAgentFontFamily: (state, action) => {
      state.data.visualization.fontFamily = action.payload;
    },

    // logo related
    setAgentLogo: (state, action) => {
      state.data.companyLogo = action.payload;
    },

    setAgnetLogoScaleDesktop: (state, action) => {
      state.data.visualization.logoScale = action.payload;
    },

    setAgnetLogoScaleMobile: (state, action) => {
      state.data.visualization.logoScaleMobile = action.payload;
    },

    setAgentShowLogo: (state, action) => {
      state.data.visualization.showLogo = action.payload;
    },

    // More settings related
    setAgentDisclaimerText: (state, action) => {
      state.data.campaignData.disclaimer = action.payload;
    },
    setAgentPoweredByBridged: (state, action) => {
      state.data.campaignData.poweredByBridged = action.payload;
    },
    setAgentZIndex: (state, action) => {
      state.data.visualization.zIndex = action.payload;
    },
    setAgentBackgroundPosition: (state, action) => {
      state.data.visualization.backgroundPosition = action.payload;
    },
    setAgentCornerRadius: (state, action) => {
      state.data.visualization.cardStyle.borderRadius = action.payload;
    },
    setAgentMargin: (state, action) => {
      state.data.visualization.cardStyle.margin = action.payload;
    },
    setAgentShadow: (state, action) => {
      state.data.visualization.cardStyle.boxShadow = action.payload;
    },
    setAgentFeedbackConfig: (state, action) => {
      state.data.campaignData.feedbackConfig = action.payload;
    },

    // * end screen related
    setAgentEndScreen: (state, action) => {
      state.data.campaignData.endScreen = action.payload;
    },

    setIsNewEndScreenOpen: (state, action) => {
      state.etc.endScreen.isNewEndScreenOpen = action.payload;
    },

    setIsAddEndScreenOpen: (state, action) => {
      state.etc.endScreen.isAddEndScreenOpen = action.payload;
    },

    // * engagement related

    setAgentGatedEngagement: (state, action) => {
      state.data.campaignData.gatedEngagement = action.payload;
    },

    setAgentShowActionAfterResponses: (state, action) => {
      state.data.campaignData.showActionAfterResponses = action.payload;
    },

    setEngagementsABTest: (state, action) => {
      state.data.campaignData.engagementsABTest = action.payload;
    },

    setAgentEngagements: (state, action) => {
      state.data.campaignData.engagements = action.payload;
      prunePollCtaMappedOptions(state);
    },

    setNumberOfAIEngagements: (state, action) => {
      state.etc.numberOfAIEngagements = action.payload;
    },

    setEditAgentEngagement: (state, action) => {
      const isEngagementInArray = state.data.campaignData.engagements.some(
        engagement => engagement._id === action.payload._id
      );
      if (isEngagementInArray) {
        state.data.campaignData.engagements = state.data.campaignData.engagements.map(engagement =>
          engagement._id === action.payload._id ? action.payload : engagement
        );
        prunePollCtaMappedOptions(state);
      }
    },

    setIsNewEngagementOpen: (state, action) => {
      state.etc.engagement.isNewEngagementOpen = action.payload;
    },

    setIsAddEngagementOpen: (state, action) => {
      state.etc.engagement.isAddEngagementOpen = action.payload;
    },

    setEngagementGenerationByAI: (state, action) => {
      state.data.campaignData.engagementGenerationByAI = action.payload;
    },

    setEngagementGenerationByAICount: (state, action) => {
      state.data.campaignData.engagementGenerationByAICount = action.payload;
    },

    setEngagementGeneratorConfigurations: (state, action) => {
      state.data.campaignData.engagementGeneratorConfigurations = action.payload;
      state.data.aiEngagementsNeedUpdate = true;
    },

    setAutoApproval: (state, action) => {
      state.data.campaignData.engagementGeneratorConfigurations.autoApproval = action.payload;
      state.data.aiEngagementsNeedUpdate = true;
    },

    setXrayQuestions: (state, action) => {
      state.data.campaignData.xrayQuestions = action.payload;
      state.data.aiEngagementsNeedUpdate = true;
    },

    setAiSimilarityActionBucketId: (state, action) => {
      state.data.campaignData.aiSimilarityActionBucketId = action.payload;
    },

    setLinkActions: (state, action) => {
      state.data.campaignData.linkActions = action.payload;
    },

    setEditAgentLinkCTA: (state, action) => {
      const isLinkActionInArray = state.data.campaignData.linkActions.some(
        linkAction => String(linkAction._id) === String(action.payload._id)
      );
      if (isLinkActionInArray) {
        state.data.campaignData.linkActions = state.data.campaignData.linkActions.map(linkAction =>
          String(linkAction._id) === String(action.payload._id) ? action.payload : linkAction
        );
      }
    },

    setEngagementSelection: (state, action) => {
      state.etc.engagementSelection = action.payload;
    },

    // * cta related

    setAgentPaywall: (state, action) => {
      state.data.campaignData.hasPaywall = action.payload;
    },

    setctaPreviewConfig: (state, action) => {
      state.data.campaignData.ctaPreviewConfig = action.payload;
    },

    setAgentCTA: (state, action) => {
      state.data.campaignData.actions = action.payload;
    },

    setAgentStaticReferences: (state, action) => {
      state.data.campaignData.staticReferences = action.payload;
    },

    setEditAgentCTA: (state, action) => {
      const updatedCta = action.payload;
      if (!updatedCta?._id) return;

      const matchesId = cta => cta && String(cta._id) === String(updatedCta._id);
      const replaceInList = list =>
        Array.isArray(list) ? list.map(cta => (matchesId(cta) ? updatedCta : cta)) : list;

      state.data.campaignData.actions = replaceInList(state.data.campaignData.actions);
      state.data.campaignData.staticReferences = replaceInList(
        state.data.campaignData.staticReferences
      );
      state.data.campaignData.linkActions = replaceInList(state.data.campaignData.linkActions);

      if (matchesId(state.data.campaignData.emailCollectionScreen)) {
        state.data.campaignData.emailCollectionScreen = updatedCta;
      }

      if (state.etc.pollCtaMapping) {
        state.etc.pollCtaMapping.ABtestActions = replaceInList(
          state.etc.pollCtaMapping.ABtestActions
        );

        if (Array.isArray(state.etc.pollCtaMapping.mappedOptions)) {
          state.etc.pollCtaMapping.mappedOptions = state.etc.pollCtaMapping.mappedOptions.map(
            entry => ({
              ...entry,
              actionIds: replaceInList(entry.actionIds),
            })
          );
        }
      }

      const ctasConfig = state.data.campaignData.personalizationTool?.ctasConfig;
      if (ctasConfig) {
        if (matchesId(ctasConfig.userInputCTAId)) {
          ctasConfig.userInputCTAId = updatedCta;
        }
        if (matchesId(ctasConfig.registrationCTAId)) {
          ctasConfig.registrationCTAId = updatedCta;
        }
      }
    },

    setAgentEmailCollection: (state, action) => {
      state.data.campaignData.emailCollectionScreen = action.payload;
    },

    setNumActionsToShow: (state, action) => {
      state.data.campaignData.numActionsToShow = action.payload;
    },
    setActionExpirationInDays: (state, action) => {
      state.data.campaignData.actionExpirationInDays = action.payload;
    },

    setActionsABTest: (state, action) => {
      state.data.campaignData.actionsABTest = action.payload;
    },

    setIsNewCTAOpen: (state, action) => {
      state.etc.cta.isNewCTAOpen = action.payload;
    },

    setIsAddCTAOpen: (state, action) => {
      state.etc.cta.isAddCTAOpen = action.payload;
    },

    setIsNewIntentCTAOpen: (state, action) => {
      state.etc.cta.isNewIntentCTAOpen = action.payload;
    },

    setIsAddIntentCTAOpen: (state, action) => {
      state.etc.cta.isAddIntentCTAOpen = action.payload;
    },

    setIsNewLinkCTAOpen: (state, action) => {
      state.etc.cta.isNewLinkCTAOpen = action.payload;
    },

    setIsAddLinkCTAOpen: (state, action) => {
      state.etc.cta.isAddLinkCTAOpen = action.payload;
    },

    setIsNewStaticCTAOpen: (state, action) => {
      state.etc.cta.isNewStaticCTAOpen = action.payload;
    },

    setIsAddStaticCTAOpen: (state, action) => {
      state.etc.cta.isAddStaticCTAOpen = action.payload;
    },

    setIsShowStaticCTAs: (state, action) => {
      state.etc.cta.isShowStaticCTAs = action.payload;
    },

    setIsNewCollectionOpen: (state, action) => {
      state.etc.collections.isNewCollectionOpen = action.payload;
    },

    setIsAddCollectionOpen: (state, action) => {
      state.etc.collections.isAddCollectionOpen = action.payload;
    },

    setIsAddAISimilarityCollectionOpen: (state, action) => {
      state.etc.collections.isAddAISimilarityCollectionOpen = action.payload;
    },

    setIsAddPersonalizationCollectionOpen: (state, action) => {
      state.etc.collections.isAddPersonalizationCollectionOpen = action.payload;
    },

    setAgentRagEngagementTitle: (state, action) => {
      state.data.campaignData.engagementTitle = action.payload;
    },
    setAgentRagEngagementDescription: (state, action) => {
      state.data.campaignData.engagementDescription = action.payload;
    },

    setAgentTextAreaPlaceHolder: (state, action) => {
      state.data.campaignData.textAreaPlaceHolder = action.payload;
    },

    setAgentButtonText: (state, action) => {
      state.data.campaignData.buttonText = action.payload;
    },
    setAgentCloseButtonPlacement: (state, action) => {
      state.data.campaignData.closeButtonPlacement = action.payload;
    },
    setAgentReferenceSectionHeader: (state, action) => {
      state.data.campaignData.referenceSectionHeader = action.payload;
    },

    setAgentAutoExpansion: (state, action) => {
      state.data.campaignData.autoExpansion = action.payload;
    },

    setAgentRagConfig: (state, action) => {
      state.data.campaignData.ragConfig = action.payload;
    },

    setAgentFooterConfig: (state, action) => {
      state.data.campaignData.footerConfig = action.payload;
    },

    setAgentArticleRelatedQuestions: (state, action) => {
      state.data.campaignData.ragConfig.articleRelatedQuestions = action.payload;
    },
    setAgentDEAArticleRelatedQuestions: (state, action) => {
      state.data.campaignData.articleRelatedQuestions = action.payload;
    },

    setAgentActionBucketId: (state, action) => {
      state.data.campaignData.actionBucketId = action.payload;
    },

    setAgentActionBucketIds: (state, action) => {
      state.data.campaignData.actionBucketIds = action.payload;
    },

    setEngagementTemplate: (state, action) => {
      state.data.campaignData.engagementGeneratorConfigurations.engagementTemplate = action.payload;
    },

    //* voice conversation related
    // Turning voice on or off also moves the agent on or off Smart Chat Aurora · Voice: the
    // template is smartChatVoice exactly when voice is on. Off while on the voice template goes
    // back to Smart Chat Aurora; off from another template (picked in Design → Template) keeps it.
    setAgentVoiceIsActive: (state, action) => {
      const campaignData = state.data.campaignData;
      const isActive = Boolean(action.payload);
      const configs = campaignData.engagementGeneratorConfigurations;
      const wasActive = campaignData.voiceConfig?.isActive === true;
      const previousLang = campaignData.voiceConfig?.lang;
      campaignData.voiceConfig = {
        ...VOICE_CONFIG_ON,
        ...(campaignData.voiceConfig || {}),
        isActive,
      };
      // An older agent turned on while still on the default voice starts on its language's voice,
      // once: after that (or after a pick in the drawer) the voice is the organiser's choice.
      if (
        isActive &&
        !wasActive &&
        !state.etc.voiceLangChosen &&
        (!previousLang || previousLang === VOICE_CONFIG_ON.lang)
      ) {
        campaignData.voiceConfig.lang = voiceForCampaignLanguage(
          state.data.visualization?.language
        );
      }
      if (isActive) {
        state.etc.voiceLangChosen = true;
        configs.engagementTemplate = KA_SMART_CHAT_VOICE_TEMPLATE;
      } else if (configs.engagementTemplate === KA_SMART_CHAT_VOICE_TEMPLATE) {
        configs.engagementTemplate = KA_SMART_CHAT_AURORA_TEMPLATE;
      }
      state.data.aiEngagementsNeedUpdate = true;
    },

    // The drawer's settings (how the card opens, read answers out loud, voice). Not the switch.
    setAgentVoiceConfig: (state, action) => {
      const current = state.data.campaignData.voiceConfig || VOICE_CONFIG_OFF;
      state.data.campaignData.voiceConfig = {
        ...current,
        ...action.payload,
        isActive: current.isActive,
      };
      if (action.payload?.lang) {
        state.etc.voiceLangChosen = true;
      }
    },

    //* linked intents related
    setAddLinkedIntent: (state, action) => {
      state.data.campaignData.linkedIntents.push({
        intent: action.payload.intent,
        actionId: '',
      });
    },

    setCurrentSelectedIntent: (state, action) => {
      state.etc.currentSelectedIntent = action.payload;
    },

    setUpdateLinkedIntentActionId: (state, action) => {
      const { intent, actionId } = action.payload;
      const linkedIntent = state.data.campaignData.linkedIntents.find(
        item => item.intent === intent
      );
      if (linkedIntent) {
        linkedIntent.actionId = actionId;
        // Also update the currentSelectedIntent if it matches
        if (state.etc.currentSelectedIntent?.intent === intent) {
          state.etc.currentSelectedIntent.actionId = actionId;
        }
      }
    },

    // * poll cta mapping related
    setAgentPollCTAsUsageType: (state, action) => {
      state.etc.pollCtaMapping.pollCTAsUsageType = action.payload;
    },

    setAgentPollCtaMappingABTestCTA: (state, action) => {
      state.etc.pollCtaMapping.ABtestActions = action.payload;
    },

    setAgentPollCtaMappingMappedOptions: (state, action) => {
      state.etc.pollCtaMapping.mappedOptions = action.payload;
    },

    setIsNewABTestCtaOpen: (state, action) => {
      state.etc.pollCtaMapping.ctaStates.isNewABTestCtaOpen = action.payload;
    },

    setIsAddABTestCtaOpen: (state, action) => {
      state.etc.pollCtaMapping.ctaStates.isAddABTestCtaOpen = action.payload;
    },

    setIsNewMappingCtaOpen: (state, action) => {
      state.etc.pollCtaMapping.ctaStates.isNewMappingCtaOpen = action.payload;
    },

    setIsAddMappingCtaOpen: (state, action) => {
      state.etc.pollCtaMapping.ctaStates.isAddMappingCtaOpen = action.payload;
    },

    //--------------------------------ai agent related--------------------------------

    setAIAgentData: (state, action) => {
      state.aiAgent = action.payload;
    },

    setAgentType: (state, action) => {
      state.etc.agentType = action.payload;
    },

    setAgentShowMandatoryCTA: (state, action) => {
      state.etc.showMandatoryCTA = action.payload;
      // Clear gated response CTA config immediately when turned off (drawer may be unmounted)
      if (!action.payload && state.data.campaignData.campaignType === 'rag') {
        state.data.campaignData.gatedEngagement = false;
        state.data.campaignData.showActionAfterResponses = 0;
        state.data.campaignData.actions = [];
        state.data.campaignData.emailCollectionScreen = null;
      }
    },

    setIntentBasedCtaIsActive: (state, action) => {
      state.etc.intentBasedCtaIsActive = action.payload;
    },

    setAIAgentName: (state, action) => {
      state.aiAgent.title = action.payload;
    },

    setAIAgentDescription: (state, action) => {
      state.aiAgent.description = action.payload;
    },

    setGAPropertyId: (state, action) => {
      state.aiAgent.aiAgentData.gaPropertyId = action.payload;
    },

    setEngineId: (state, action) => {
      state.data.campaignData.engineId = action.payload;
    },

    setEngineVersionId: (state, action) => {
      state.data.campaignData.engineVersionId = action.payload;
    },

    setIsNewEngineOpen: (state, action) => {
      state.etc.engine.isNewEngineOpen = action.payload;
    },
    // A matching engine just published from the builder over the Connection engine picker: the
    // picker selects it once it's listed, then clears this.
    setNewMatchingEngineId: (state, action) => {
      state.etc.engine.newMatchingEngineId = action.payload;
    },

    setIsAddEngineOpen: (state, action) => {
      state.etc.engine.isAddEngineOpen = action.payload;
    },

    setEngineList: (state, action) => {
      state.etc.engine.engineList = action.payload;
    },

    setAiEngagementsNeedUpdate: (state, action) => {
      state.data.aiEngagementsNeedUpdate = action.payload;
    },

    // * activation schedule related
    setActivationScheduleConfig: (state, action) => {
      state.data.activationScheduleConfig = action.payload;
    },
    //--------------------------------smart reply related--------------------------------
    setSmartReplyData: (state, action) => {
      state.smartReply = action.payload;
    },

    setSmartReplyDataObject: (state, action) => {
      state.smartReply.smartReplyData = action.payload;
    },
    setSmartReplyName: (state, action) => {
      state.smartReply.title = action.payload;
    },
    setSmartReplyEngineId: (state, action) => {
      state.smartReply.smartReplyData.engineId = action.payload;
    },
    setSmartReplyLanguage: (state, action) => {
      state.smartReply.language = action.payload;
    },
    setSmartReplyType: (state, action) => {
      state.smartReply.smartReplyData.type = action.payload;
    },
    setSmartReplyActionBucketId: (state, action) => {
      state.smartReply.smartReplyData.actionBucketId = action.payload;
    },
    setSmartReplyActionBucketIds: (state, action) => {
      state.smartReply.smartReplyData.actionBucketIds = action.payload;
    },
    setSmartReplyProtocolType: (state, action) => {
      state.smartReply.smartReplyData.protocol.type = action.payload;
    },
    setSmartReplyProtocolConfig: (state, action) => {
      const { field, value } = action.payload;
      state.smartReply.smartReplyData.protocol[field] = value;
    },
    setSmartReplyApprovalConfig: (state, action) => {
      const { field, value } = action.payload;
      state.smartReply.smartReplyData.replyApprovalConfig[field] = value;
    },
    setSmartReplyEmailTemplateRenderFormat: (state, action) => {
      state.smartReply.smartReplyData.emailTemplate.renderFormat = action.payload;
    },
    setSmartReplyEmailTemplateFooter: (state, action) => {
      state.smartReply.smartReplyData.emailTemplate.footer = action.payload;
    },

    // whatsapp KA related
    setWhatsappKAProvider: (state, action) => {
      if (!state.smartReply.smartReplyData.providerData) {
        state.smartReply.smartReplyData.providerData = {
          type: action.payload,
          accountSID: '',
          authToken: '',
          phoneNumber: '',
        };
      } else {
        state.smartReply.smartReplyData.providerData.type = action.payload;
      }
    },
    setWhatsappKAIntegrationId: (state, action) => {
      state.smartReply.smartReplyData.integrationId = action.payload || null;
    },
    setWhatsappKAAccountSID: (state, action) => {
      state.smartReply.smartReplyData.providerData.accountSID = action.payload;
    },
    setWhatsappKAAuthToken: (state, action) => {
      state.smartReply.smartReplyData.providerData.authToken = action.payload;
    },
    setWhatsappKAPhoneNumber: (state, action) => {
      state.smartReply.smartReplyData.providerData.phoneNumber = action.payload;
    },

    // * personalization agent (ptEmailChannel) related
    setPersonalizationAgentTitle: (state, action) => {
      state.personalizationAgent.title = action.payload;
    },
    setPersonalizationAgentType: (state, action) => {
      state.personalizationAgent.type = action.payload;
    },
    setPersonalizationAgentCollection: (state, action) => {
      const collection = action.payload;
      state.personalizationAgent.collectionId = collection?._id || null;
      state.personalizationAgent.collectionTitle = collection?.title || null;
    },
    setPersonalizationAgentEngine: (state, action) => {
      const engine = action.payload;
      state.personalizationAgent.engineId = engine?._id || null;
      state.personalizationAgent.engineTitle = engine?.title || null;
    },
    setPersonalizationAgentRequiredFields: (state, action) => {
      state.personalizationAgent.requiredFields = action.payload;
    },
    setPersonalizationAgentProfileImport: (state, action) => {
      state.personalizationAgent.profileImport = {
        ...state.personalizationAgent.profileImport,
        ...action.payload,
      };
    },
    resetPersonalizationAgentProfileImport: state => {
      state.personalizationAgent.profileImport = {
        source: null,
        importId: null,
        mediaId: null,
        fileName: null,
        integrationId: null,
      };
    },

    // * connection agent related
    setConnectionAgentTitle: (state, action) => {
      state.connectionAgent.title = action.payload;
    },
    // A whole setup at once: editing an agent after it's made, or continuing a draft.
    setConnectionAgentState: (state, action) => {
      const { formats } = action.payload || {};
      state.connectionAgent = {
        ...state.connectionAgent,
        ...action.payload,
        // A draft saved with the engine's 'sessions' gets the slot's 'session_recs'.
        ...(Array.isArray(formats)
          ? { formats: formats.map(slot => ({ ...slot, format: agentFormatOf(slot?.format) })) }
          : {}),
      };
    },
    // One to one runs one slot per pairing (D2): turn a pairing on or off. The last one stays.
    toggleConnectionAgentPairing: (state, action) => {
      const pairing = action.payload;
      const formats = state.connectionAgent.formats;
      const pairs = formats.filter(item => item.format === 'one_to_one');
      const exists = pairs.some(item => item.pairing === pairing);
      if (exists && pairs.length === 1) return;
      const key = `one_to_one:${pairing}`;
      if (exists) {
        keepRemovedSlot(
          state,
          key,
          pairs.find(item => item.pairing === pairing)
        );
        state.connectionAgent.formats = formats.filter(
          item => !(item.format === 'one_to_one' && item.pairing === pairing)
        );
      } else {
        state.connectionAgent.formats = [
          ...formats,
          takeRemovedSlot(state, key) || {
            format: 'one_to_one',
            pairing,
            engineId: null,
            engineTitle: null,
          },
        ];
      }
    },
    toggleConnectionAgentFormat: (state, action) => {
      const format = action.payload;
      const exists = state.connectionAgent.formats.some(item => item.format === format);
      if (exists) {
        state.connectionAgent.formats
          .filter(item => item.format === format)
          .forEach(item =>
            keepRemovedSlot(
              state,
              format === 'one_to_one' ? `one_to_one:${item.pairing}` : format,
              item
            )
          );
        state.connectionAgent.formats = state.connectionAgent.formats.filter(
          item => item.format !== format
        );
      } else {
        // One to one comes back with the pairings it had, each with its engine.
        const keys =
          format === 'one_to_one'
            ? Object.keys(state.connectionAgent.removedSlots || {}).filter(key =>
                key.startsWith('one_to_one:')
              )
            : [format];
        const restored = keys.map(key => takeRemovedSlot(state, key)).filter(Boolean);
        state.connectionAgent.formats = [
          ...state.connectionAgent.formats,
          ...(restored.length
            ? restored
            : [
                {
                  format,
                  pairing: format === 'one_to_one' ? 'delegate_sponsor' : null,
                  engineId: null,
                  engineTitle: null,
                },
              ]),
        ];
      }
    },
    // Changes one slot, named by its key (`one_to_one:delegate_sponsor`) or, for the other formats, its format.
    setConnectionAgentFormat: (state, action) => {
      const { key, format, ...changes } = action.payload;
      const slotKey = item =>
        item.format === 'one_to_one' ? `one_to_one:${item.pairing}` : item.format;
      state.connectionAgent.formats = state.connectionAgent.formats.map(item =>
        (key ? slotKey(item) === key : item.format === format) ? { ...item, ...changes } : item
      );
    },
    setConnectionAgentSessionsCollection: (state, action) => {
      const collection = action.payload;
      state.connectionAgent.sessionsCollectionId = collection?._id || null;
      state.connectionAgent.sessionsCollectionTitle = collection?.title || null;
    },
    // The attendee collection the agent matches, picked from the Event Database (D38, D39).
    setConnectionAgentAttendeeCollection: (state, action) => {
      const collection = action.payload;
      state.connectionAgent.attendeeCollectionId = collection?._id || null;
      state.connectionAgent.attendeeCollectionTitle = collection?.title || null;
    },
    setConnectionAgentRules: (state, action) => {
      state.connectionAgent.rules = { ...state.connectionAgent.rules, ...action.payload };
    },
    setConnectionAgentMeetings: (state, action) => {
      state.connectionAgent.meetings = { ...state.connectionAgent.meetings, ...action.payload };
    },

    // * qualification related
    setQualificationAgentName: (state, action) => {
      state.qualificationAgent.title = action.payload;
    },

    setIsScriptAdded: (state, action) => {
      state.etc.isScriptAdded = action.payload;
    },

    setQualificationAgentStageType: (state, action) => {
      state.etc.stageType = action.payload;
    },

    setQualificationAgentStages: (state, action) => {
      state.qualificationAgent.stages = action.payload;
    },

    setQualificationSignals: (state, action) => {
      state.qualificationAgent.signals = action.payload;
    },

    setQualificationAgentTopFunnel: (state, action) => {
      state.qualificationAgent.funnel.stages[0] = action.payload;
    },

    setQualificationAgentMiddleFunnel: (state, action) => {
      state.qualificationAgent.funnel.stages[1] = action.payload;
    },

    setQualificationAgentBottomFunnel: (state, action) => {
      state.qualificationAgent.funnel.stages[2] = action.payload;
    },

    setQualificationAgentData: (state, action) => {
      state.qualificationAgent = action.payload;
    },

    // * qualification where (domain) related

    setQualificationAgentDomainConfig: (state, action) => {
      state.qualificationAgent.domainConfig = action.payload;
    },

    setQualificationAgentDomainType: (state, action) => {
      state.qualificationAgent.domainConfig.type = action.payload;
    },

    setQualificationAgentDomain: (state, action) => {
      state.qualificationAgent.domainConfig.domainId = action.payload;
    },

    setQualificationAgentDomainPaths: (state, action) => {
      state.qualificationAgent.domainConfig.paths = action.payload;
    },

    setQualificationAgentDomainExceptions: (state, action) => {
      state.qualificationAgent.domainConfig.exceptions = action.payload;
    },

    setQualificationPageMetaDataDomainConfigFromPublishedDate: (state, action) => {
      state.qualificationAgent.domainConfig.pageMetaDataDomainConfig.fromPublishedDate =
        action.payload;
    },

    setQualificationPageMetaDataDomainConfigCategories: (state, action) => {
      state.qualificationAgent.domainConfig.pageMetaDataDomainConfig.categories = action.payload;
    },

    setQualificationPageMetaDataDomainConfigTags: (state, action) => {
      state.qualificationAgent.domainConfig.pageMetaDataDomainConfig.tags = action.payload;
    },

    // * is agent edit loading related
    setIsAgentEditLoading: (state, action) => {
      state.etc.isAgentEditLoading = action.payload;
    },

    // * brand sync related
    setBrandSyncDomainId: (state, action) => {
      state.etc.brandSync.domainId = action.payload;
    },
    setBrandSyncSamplePageUrl: (state, action) => {
      state.etc.brandSync.samplePageUrl = action.payload;
    },
    setBrandSyncSuggestions: (state, action) => {
      state.etc.brandSync.brandInfo = action.payload;
    },
    setBrandSyncHasInitialFetch: (state, action) => {
      state.etc.brandSync.hasInitialFetch = action.payload;
    },
    setBrandSyncAppliedEventId: (state, action) => {
      state.etc.brandSync.appliedEventId = action.payload;
    },
    setSelectedBrandSyncProperties: (state, action) => {
      state.etc.selectedBrandSyncProperties = action.payload;
    },

    // * personalization tool related
    setPersonalizationType: (state, action) => {
      state.data.campaignData.personalizationTool.type = action.payload;
    },
    setPersonalizationToolIsActive: (state, action) => {
      state.data.campaignData.personalizationTool.isActive = action.payload;
    },
    setPersonalizationToolCtasConfig: (state, action) => {
      const pt = state.data.campaignData.personalizationTool;
      if (!pt.ctasConfig) {
        pt.ctasConfig = {
          userInputCTAId: null,
          registrationCTAId: null,
          hasExportResult: false,
        };
      }
      Object.assign(pt.ctasConfig, action.payload);
    },
    setPersonalizationToolRequiredFields: (state, action) => {
      state.data.campaignData.personalizationTool.requiredFields = action.payload;
    },
    setPersonalizationToolActionBucketIds: (state, action) => {
      state.data.campaignData.personalizationTool.actionBucketIds = action.payload;
    },
    setIsAddUserInputPersonalizationCTAOpen: (state, action) => {
      if (!state.etc.personalizationCta) {
        state.etc.personalizationCta = {
          isAddUserInputCTAOpen: false,
          isAddRegistrationCTAOpen: false,
          isKAPersonalizationNewCtaOpen: false,
          kaPersonalizationNewCtaTargetSlot: null,
        };
      }
      state.etc.personalizationCta.isAddUserInputCTAOpen = action.payload;
    },
    setIsAddRegistrationPersonalizationCTAOpen: (state, action) => {
      if (!state.etc.personalizationCta) {
        state.etc.personalizationCta = {
          isAddUserInputCTAOpen: false,
          isAddRegistrationCTAOpen: false,
          isKAPersonalizationNewCtaOpen: false,
          kaPersonalizationNewCtaTargetSlot: null,
        };
      }
      state.etc.personalizationCta.isAddRegistrationCTAOpen = action.payload;
    },
    setIsKAPersonalizationNewCtaOpen: (state, action) => {
      if (!state.etc.personalizationCta) {
        state.etc.personalizationCta = {
          isAddUserInputCTAOpen: false,
          isAddRegistrationCTAOpen: false,
          isKAPersonalizationNewCtaOpen: false,
          kaPersonalizationNewCtaTargetSlot: null,
        };
      }
      state.etc.personalizationCta.isKAPersonalizationNewCtaOpen = action.payload;
    },
    setKAPersonalizationNewCtaTargetSlot: (state, action) => {
      if (!state.etc.personalizationCta) {
        state.etc.personalizationCta = {
          isAddUserInputCTAOpen: false,
          isAddRegistrationCTAOpen: false,
          isKAPersonalizationNewCtaOpen: false,
          kaPersonalizationNewCtaTargetSlot: null,
        };
      }
      state.etc.personalizationCta.kaPersonalizationNewCtaTargetSlot = action.payload;
    },
    setLockResult: (state, action) => {
      state.data.campaignData.personalizationTool.lockResult = action.payload;
    },
    setIsPTStreaming: (state, action) => {
      state.data.campaignData.personalizationTool.isPTStreaming = action.payload;
    },
    setPersonalizationToolInterfaceVariant: (state, action) => {
      state.data.campaignData.personalizationTool.interfaceVariant = action.payload;
    },
    setPersonalizationToolExportEmailProvider: (state, action) => {
      const pt = state.data.campaignData.personalizationTool;
      if (!pt.exportEmailConfig) {
        pt.exportEmailConfig = {
          provider: 'bridged',
          mailbox: '',
          clientId: '',
          clientSecret: '',
          authority: '',
        };
      }
      pt.exportEmailConfig.provider = action.payload;
    },
    setPersonalizationToolExportEmailMailbox: (state, action) => {
      const pt = state.data.campaignData.personalizationTool;
      if (!pt.exportEmailConfig) {
        pt.exportEmailConfig = {
          provider: 'bridged',
          mailbox: '',
          clientId: '',
          clientSecret: '',
          authority: '',
        };
      }
      pt.exportEmailConfig.mailbox = action.payload;
    },
    setPersonalizationToolExportEmailClientId: (state, action) => {
      const pt = state.data.campaignData.personalizationTool;
      if (!pt.exportEmailConfig) {
        pt.exportEmailConfig = {
          provider: 'bridged',
          mailbox: '',
          clientId: '',
          clientSecret: '',
          authority: '',
        };
      }
      pt.exportEmailConfig.clientId = action.payload;
    },
    setPersonalizationToolExportEmailClientSecret: (state, action) => {
      const pt = state.data.campaignData.personalizationTool;
      if (!pt.exportEmailConfig) {
        pt.exportEmailConfig = {
          provider: 'bridged',
          mailbox: '',
          clientId: '',
          clientSecret: '',
          authority: '',
        };
      }
      pt.exportEmailConfig.clientSecret = action.payload;
    },
    setPersonalizationToolExportEmailAuthority: (state, action) => {
      const pt = state.data.campaignData.personalizationTool;
      if (!pt.exportEmailConfig) {
        pt.exportEmailConfig = {
          provider: 'bridged',
          mailbox: '',
          clientId: '',
          clientSecret: '',
          authority: '',
        };
      }
      pt.exportEmailConfig.authority = action.payload;
    },
    setCtaWebhookIntegrationConfig: (state, action) => {
      state.data.campaignData.ctaWebhookIntegrationConfig = action.payload;
    },
  },
});

export const {
  setAgentData,
  setAgentEtc,
  setAgentStep,
  setNextAgentStep,
  setPrevAgentStep,
  setGoalSubStep,
  setSelectedPlaybook,
  setPlaybookId,

  //goal related
  setAgentGoal,

  //define related
  setAgentDomainConfig,
  setAgentDomainType,
  setAgentName,
  setAgentDomain,
  setAgentDomainPaths,
  setAgentDomainExceptions,
  setQualificationPageMetaDataDomainConfigFromPublishedDate,
  setQualificationPageMetaDataDomainConfigCategories,
  setQualificationPageMetaDataDomainConfigTags,
  setPageMetaDataDomainConfigFromPublishedDate,
  setPageMetaDataDomainConfigCategories,
  setPageMetaDataDomainConfigTags,
  setAgentLanguage,

  //--------------------------------design related--------------------------------
  // * placement related
  setCurrentSelectedPlacement,
  setCampaignPlacements,
  setSidePanelButtonStyleDesktop,
  setSidePanelButtonStyleMobile,
  setSidePanelButtonPositionDesktop,
  setSidePanelButtonPositionMobile,
  setSidePanelButtonTopDesktop,
  setSidePanelButtonTopMobile,
  setAgentSpotlightAnimation,
  setAgentSpotlightAnimationMobile,
  setAgentIgnoreCLS,
  setAgentIgnoreCLSMobile,
  setAgentEngagementViewLimit,

  // * visualization related
  setAgentVisualization,
  // Theme related
  setAgentPrimaryColor,
  setAgentDarkTheme,
  // setAgentSpotlightAnimation,
  setAgentAnimatedBorder,
  setAgentCardSizeDesktop,
  setAgentCardSizeMobile,

  // font related
  setAgentFontScaleDesktop,
  setAgentFontScaleMobile,
  setAgentFontFamily,

  // logo related
  setAgentLogo,
  setAgnetLogoScaleDesktop,
  setAgnetLogoScaleMobile,
  setAgentShowLogo,

  // More settings related
  setAgentZIndex,
  setAgentBackgroundPosition,
  setAgentCornerRadius,
  setAgentMargin,
  setAgentShadow,
  setAgentFeedbackConfig,
  setAgentDisclaimerText,
  setAgentPoweredByBridged,

  // * end screen related
  setAgentEndScreen,
  setIsNewEndScreenOpen,
  setIsAddEndScreenOpen,

  // * engagement related
  setAgentGatedEngagement,
  setAgentShowActionAfterResponses,
  setEngagementsABTest,
  setAgentEngagements,
  setEditAgentEngagement,
  setIsNewEngagementOpen,
  setIsAddEngagementOpen,
  setEngagementGenerationByAI,
  setEngagementGenerationByAICount,
  setEngagementSelection,
  setEngagementGeneratorConfigurations,
  setAutoApproval,
  setXrayQuestions,
  setAiSimilarityActionBucketId,
  setLinkActions,
  setEditAgentLinkCTA,
  setNumberOfAIEngagements,

  // * cta related
  setAgentPaywall,
  setctaPreviewConfig,
  setAgentCTA,
  setAgentStaticReferences,
  setEditAgentCTA,
  setNumActionsToShow,
  setActionsABTest,
  setIsNewIntentCTAOpen,
  setIsAddIntentCTAOpen,
  setIsNewCTAOpen,
  setIsAddCTAOpen,
  setIsNewLinkCTAOpen,
  setIsAddLinkCTAOpen,
  setAgentEmailCollection,
  setIsNewStaticCTAOpen,
  setIsAddStaticCTAOpen,
  setIsShowStaticCTAs,

  // * linked intents related
  setAddLinkedIntent,
  setCurrentSelectedIntent,
  setUpdateLinkedIntentActionId,

  // * poll cta mapping related
  setAgentPollCTAsUsageType,
  setAgentPollCtaMappingABTestCTA,
  setAgentPollCtaMappingMappedOptions,
  setIsNewABTestCtaOpen,
  setIsAddABTestCtaOpen,
  setIsNewMappingCtaOpen,
  setIsAddMappingCtaOpen,

  // * collections related
  setIsNewCollectionOpen,
  setIsAddCollectionOpen,
  setIsAddAISimilarityCollectionOpen,
  setIsAddPersonalizationCollectionOpen,
  // * knowledge agent related
  setAgentRagEngagementTitle,
  setAgentRagEngagementDescription,
  setAgentRagConfig,
  setAgentFooterConfig,
  setAgentArticleRelatedQuestions,
  setAgentDEAArticleRelatedQuestions,
  setAgentActionBucketId,
  setAgentActionBucketIds,
  setAgentTextAreaPlaceHolder,
  setAgentButtonText,
  setAgentCloseButtonPlacement,
  setAgentReferenceSectionHeader,
  setAgentAutoExpansion,
  setActionExpirationInDays,
  // * engagement template related
  setEngagementTemplate,
  setAgentVoiceIsActive,
  setAgentVoiceConfig,

  // * ai agent related
  setAgentType,
  setAIAgentData,
  setAIAgentName,
  setAIAgentDescription,
  setGAPropertyId,
  setAgentShowMandatoryCTA,
  setIntentBasedCtaIsActive,
  // * engine related
  setEngineId,
  setEngineVersionId,
  setIsNewEngineOpen,
  setNewMatchingEngineId,
  setIsAddEngineOpen,
  setEngineList,

  // * ai engagements need update
  setAiEngagementsNeedUpdate,

  // * activation schedule related
  setActivationScheduleConfig,

  // * is agent edit loading related
  setIsAgentEditLoading,
  // * smart reply related
  setSmartReplyData,
  setSmartReplyDataObject,
  setSmartReplyName,
  setSmartReplyEngineId,
  setSmartReplyLanguage,
  setSmartReplyType,
  setSmartReplyActionBucketId,
  setSmartReplyActionBucketIds,
  setSmartReplyProtocolType,
  setSmartReplyProtocolConfig,
  setSmartReplyApprovalConfig,
  setSmartReplyEmailTemplateRenderFormat,
  setSmartReplyEmailTemplateFooter,
  setWhatsappKAProvider,
  setWhatsappKAIntegrationId,
  setWhatsappKAAccountSID,
  setWhatsappKAAuthToken,
  setWhatsappKAPhoneNumber,

  // * personalization agent (ptEmailChannel) related
  setPersonalizationAgentTitle,
  setConnectionAgentTitle,
  setConnectionAgentState,
  toggleConnectionAgentPairing,
  toggleConnectionAgentFormat,
  setConnectionAgentFormat,
  setConnectionAgentSessionsCollection,
  setConnectionAgentAttendeeCollection,
  setConnectionAgentRules,
  setConnectionAgentMeetings,
  setPersonalizationAgentType,
  setPersonalizationAgentCollection,
  setPersonalizationAgentEngine,
  setPersonalizationAgentRequiredFields,
  setPersonalizationAgentProfileImport,
  resetPersonalizationAgentProfileImport,
  // * qualification related
  setQualificationAgentName,
  setIsScriptAdded,
  setQualificationAgentStageType,
  setQualificationAgentStages,
  setQualificationSignals,
  setQualificationAgentTopFunnel,
  setQualificationAgentMiddleFunnel,
  setQualificationAgentBottomFunnel,
  setQualificationAgentData,
  setQualificationAgentDomainConfig,
  setQualificationAgentDomainType,
  setQualificationAgentDomain,
  setQualificationAgentDomainPaths,
  setQualificationAgentDomainExceptions,

  // * personalization tool related
  setPersonalizationType,
  setPersonalizationToolIsActive,
  setPersonalizationToolCtasConfig,
  setPersonalizationToolRequiredFields,
  setPersonalizationToolActionBucketIds,
  setIsAddUserInputPersonalizationCTAOpen,
  setIsAddRegistrationPersonalizationCTAOpen,
  setIsKAPersonalizationNewCtaOpen,
  setKAPersonalizationNewCtaTargetSlot,
  setLockResult,
  setIsPTStreaming,
  setPersonalizationToolInterfaceVariant,
  setPersonalizationToolExportEmailProvider,
  setPersonalizationToolExportEmailMailbox,
  setPersonalizationToolExportEmailClientId,
  setPersonalizationToolExportEmailClientSecret,
  setPersonalizationToolExportEmailAuthority,
  setCtaWebhookIntegrationConfig,
  // * brand sync related
  setBrandSyncDomainId,
  setBrandSyncSamplePageUrl,
  setBrandSyncSuggestions,
  setBrandSyncHasInitialFetch,
  setBrandSyncAppliedEventId,
  setSelectedBrandSyncProperties,

  // * margin related
  setIsDefaultMargin,

  // * corner radius related
  setIsDefaultCornerRadius,
} = agentSlice.actions;

export default agentSlice.reducer;
